import { useEffect, useMemo, useRef, useState } from 'react';

import { IconCarGarage, IconCaretDownFilled } from '@tabler/icons-react';
import { useAtom } from 'jotai';

import useMarketSegments, {
  MarketSegmentsResponse,
} from 'components/Segments/api/useMarketSegments/useMarketSegments';
import segmentDeserializer from 'components/Segments/segmentDeserializer';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';
import { selectedOptionAtom, selectedSegmentAtom } from 'store/selectSegmentAtom';

import styles from './SegmentsButton.module.scss';

/** Avoid resetting while a newly chosen segment races a segments refetch. */
const SEGMENT_VALIDATION_GRACE_MS = 1500;

function isConditionOnlySegment(segment: string): boolean {
  const normalized = segment.toUpperCase();
  return normalized === 'NEW' || normalized === 'USED';
}

function segmentExistsInList(
  selectedSegment: string,
  flattenedSegments: { segment: string }[],
): boolean {
  const normalizedSelected = selectedSegment.toUpperCase();
  return flattenedSegments.some((segment) => segment.segment.toUpperCase() === normalizedSelected);
}

export default function SegmentsButton({ numberOfCharacters }: { numberOfCharacters?: number }) {
  const [selectedSegment, setSelectedSegment] = useAtom(selectedSegmentAtom);
  const [, setSelectedOption] = useAtom(selectedOptionAtom);
  const [dealerId] = useSelectedDealerId();
  const { data, isSuccess, isFetching } = useMarketSegments();
  const { addToast } = useToast();
  const prevDealerIdRef = useRef<number | undefined>(undefined);
  const segmentChangedAtRef = useRef(0);
  const prevSegmentRef = useRef(selectedSegment);
  const isDealerChangeResetRef = useRef(false);
  const [validationRecheck, setValidationRecheck] = useState(0);

  const flattenedSegments = useMemo(
    () => segmentDeserializer(data as unknown as MarketSegmentsResponse).flattenedSegments,
    [data],
  );
  const segmentName = selectedSegment.replaceAll('|', ', ');

  useEffect(() => {
    if (prevSegmentRef.current !== selectedSegment) {
      segmentChangedAtRef.current = Date.now();
      prevSegmentRef.current = selectedSegment;
    }
  }, [selectedSegment]);

  // Segment atoms are not dealer-scoped; clear on dealer change to avoid flash-then-reset.
  useEffect(() => {
    if (prevDealerIdRef.current !== undefined && prevDealerIdRef.current !== dealerId) {
      isDealerChangeResetRef.current = true;
      setSelectedSegment('NEW');
      setSelectedOption('CONDITION');
    }
    prevDealerIdRef.current = dealerId;
  }, [dealerId, setSelectedSegment, setSelectedOption]);

  useEffect(() => {
    // Intentional reset from dealer switch — do not toast or re-validate as an error.
    if (isDealerChangeResetRef.current) {
      isDealerChangeResetRef.current = false;
      return;
    }

    // Wait for a settled segment list for the current dealer before validating.
    if (!isSuccess || isFetching || flattenedSegments.length === 0) {
      return;
    }
    if (isConditionOnlySegment(selectedSegment)) {
      return;
    }
    if (segmentExistsInList(selectedSegment, flattenedSegments)) {
      return;
    }

    const msSinceChange = Date.now() - segmentChangedAtRef.current;
    if (segmentChangedAtRef.current > 0 && msSinceChange < SEGMENT_VALIDATION_GRACE_MS) {
      // Selection likely raced a refetch; re-check after the grace window with fresh data.
      const timeoutId = window.setTimeout(() => {
        setValidationRecheck((tick) => tick + 1);
      }, SEGMENT_VALIDATION_GRACE_MS - msSinceChange);

      return () => window.clearTimeout(timeoutId);
    }

    // Toast only when the selection changed recently (flash after user action).
    // Stale localStorage cleanup on load stays silent.
    const isLikelyUserFlash =
      segmentChangedAtRef.current > 0 && msSinceChange < SEGMENT_VALIDATION_GRACE_MS + 2000;

    if (isLikelyUserFlash) {
      addToast({
        status: 'error',
        description: 'Something went wrong with segment selection. Please try again.',
      });
    }

    setSelectedSegment('NEW');
    setSelectedOption('CONDITION');
  }, [
    selectedSegment,
    flattenedSegments,
    isSuccess,
    isFetching,
    dealerId,
    validationRecheck,
    addToast,
    setSelectedSegment,
    setSelectedOption,
  ]);

  return (
    <div className={styles.segmentsButtonWrapper}>
      <Button
        variant='outline'
        leftIcon={<IconCarGarage size={18} />}
        rightIcon={<IconCaretDownFilled size={16} />}
      >
        <div className={styles.segmentsButton}>
          <Text size={16} lineHeight='24px'>
            Segment:
          </Text>
          <Text size={16} lineHeight='24px' fontWeight={7}>
            {numberOfCharacters && segmentName.length > numberOfCharacters
              ? `${segmentName.slice(0, numberOfCharacters)}...`
              : segmentName}
          </Text>
        </div>
      </Button>
    </div>
  );
}
